/**
 * SetupRoute — first-launch API-key entry screen (Requirement 6.7).
 *
 * Renders when the boot sequence detects no API key from either the
 * build-time `VITE_GEMINI_API_KEY` env var or the locally persisted
 * runtime override. The End_User pastes a Gemini API key, which the
 * route persists to the active `ConversationStore` and applies through
 * `setApiKeyOverride` so subsequent `getApiKey()` calls see it without
 * a reload.
 *
 * The input is rendered as `type="password"` so the value is masked at
 * the platform level (Requirement 13.5, Property 35). The route never
 * echoes the key value back into accessible text — labels reference
 * the field generically ("Gemini API key"), the placeholder describes
 * what to enter, and the input is the only DOM node that ever holds
 * the value.
 */

import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { setApiKeyOverride } from '../infra/geminiClient';
import { getActiveConversationStore } from '../state';

import styles from './SetupRoute.module.css';

interface SetupRouteProps {
  /**
   * Invoked after the API key is persisted and applied. The App shell
   * uses this to navigate back to `/` and let the boot sequence
   * re-evaluate the `kind: 'setup' | 'ready'` decision.
   */
  onSaved: () => void;
}

export function SetupRoute({ onSaved }: SetupRouteProps): ReactElement {
  const { t } = useTranslation();
  const [key, setKey] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = key.trim();
    if (trimmed.length === 0) {
      setError(t('setup.missingKeyError'));
      return;
    }
    // Persist locally; never displayed in any UI.
    const store = getActiveConversationStore();
    await store.setApiKey(trimmed);
    setApiKeyOverride(trimmed);
    onSaved();
  };

  return (
    <main
      className={styles.setup}
      aria-labelledby="setup-heading"
      data-testid="setup-route"
    >
      <div className={styles.card}>
        <h1 id="setup-heading" className={styles.title}>
          {t('setup.title')}
        </h1>
        <p className={styles.intro}>{t('setup.intro')}</p>
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className={styles.form}
        >
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={t('setup.apiKeyPlaceholder')}
            aria-label={t('setup.apiKeyPlaceholder')}
            className={styles.input}
            autoFocus
            data-testid="setup-key-input"
          />
          <button
            type="submit"
            className={styles.btn}
            data-testid="setup-save"
          >
            {t('setup.save')}
          </button>
          {error !== null ? (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          ) : null}
        </form>
      </div>
    </main>
  );
}
